/**
 ShapeDiver 3D Viewer Application

 MaterialPropertiesGem.js

 Content:
   The definition for the gem material.

  Copyright (c) 2018 ShapeDiver GmbH

  @module MaterialPropertiesGem
 */

/**
 * Utility function to convert from all kinds of string formats to a tinycolor
 * @function
 * @ignore
 */
var toTinyColor = require('../util/toTinyColor');

/**
 * @typedef {Object} JSONGemMaterial
 * @version 1.0
 * @variation 1
 * @see module:JSONMaterial~JSONColor
 * @property {String} version - "1.0"

 * @property {Boolean} [flatShading] a boolean value to select between flat and smooth shading
 * @property {Number} [contrast] the contrast of the result of the whole shader
 * @property {Number} [directSpecularIntensity] the intensity of the direct specular component, the reflection of the light
 * @property {Number} [directSpecularContrast] the contrast of the direct specular component, the reflection of the light
 * @property {Number} [indirectSpecularIntensity] the intensity of the indirect specular component, the environment map
 * @property {Number} [indirectSpecularContrast] the contrast of the indirect specular component, the environment map

 * @property {module:JSONMaterial~JSONColor} [colorFront] the color of the front faces
 * @property {module:JSONMaterial~JSONColor} [colorBack] the color of the back faces
 * @property {Number} [opacityFront] the opacity of the front faces
 * @property {Number} [opacityBack] the opacity of the back faces
 * @property {Number} [metalnessFront] the metalness of the front faces
 * @property {Number} [metalnessBack] the metalness of the back faces
 * @property {Number} [roughnessFront] the roughness of the front faces
 * @property {Number} [roughnessBack] the roughness of the back faces

 * @property {module:JSONMaterial~JSONTexture} [reflectionMapFront] the map that is added to simulate reflections and refractions of the stone on the front face, the result is added to the overall color
   The map is not read out via the texture coordinate, but with the reflected vector that is projected onto a cube which has this texture applied to all sides.
 * @property {Number} [reflectionMapRepeatFront] the repeat value for the map
 * @property {module:JSONMaterial~JSONColor} [reflectionMapColorFront] an additional color value that is multiplied with the color that is read out from the map
 * @property {Number} [reflectionMapIntensityFront] the intensity for the map
 * @property {Number} [reflectionMapContrastFront] the contrast for the map

 * @property {module:JSONMaterial~JSONTexture} [reflectionNormalMap] a normal map that can be added to influence how the reflection on the front face will be sampled (reflectionMapFront)
   This map is sampled  in a similar fashion to how the reflectionMapFront is sampled. The resulting normal is then used to reflect the already reflected direction to receive the perception of deeper reflections and refractions.
 * @property {Number} [reflectionNormalMapRepeat] the repeat value for the map
 * @property {Number} [reflectionNormalMapIntensity] the intensity value for the map

 * @property {module:JSONMaterial~JSONTexture} [reflectionMapBack] the map that is added to simulate reflections and refractions of the stone on the back face, the result is added to the overall color
   The map is not read out via the texture coordinate, but with the reflected vector that is projected onto a cube which has this texture applied to all sides.
 * @property {Number} [reflectionMapRepeatBack] the repeat value for the map
 * @property {module:JSONMaterial~JSONColor} [reflectionMapColorBack] an additional color value that is multiplied with the color that is read out from the map
 * @property {Number} [reflectionMapIntensityBack] the intensity for the map
 * @property {Number} [reflectionMapContrastBack] the contrast for the map

 * @property {module:JSONMaterial~JSONTexture} [dispersionMap] an additional color map to simulate dispersion
   This map contains small color dots that are added to the overall result of the shader. These colors will be perceived as seemingly random occurrences of dispersion in the stone.
 * @property {Number} [dispersionMapRepeat] the repeat value for the map
 * @property {Number} [dispersionMapIntensity] the intensity value for the map
 */

/**
 * 
 */
var MaterialPropertiesGem = function () {
  let that = this;
  that.materialType = 'gem';

  that.flatShading = false;
  that.transparent = true;

  that.contrast = 1.0;
  that.directSpecularContrast = 1.0;
  that.indirectSpecularContrast = 1.0;

  that.directSpecularIntensity = 1.0;
  that.indirectSpecularIntensity = 1.0;

  that.colorFront = toTinyColor('ffffffff').toThreeColor();
  that.colorBack = toTinyColor('ffffffff').toThreeColor();

  that.opacityFront = 0.5;
  that.opacityBack = 1.0;

  that.metalnessFront = 1.0;
  that.metalnessBack = 1.0;
  //that.metalnessMapFront = null;
  //that.metalnessMapBack = null;

  that.roughnessFront = 0.0;
  that.roughnessBack = 0.0;
  //that.roughnessMapFront = null;
  //that.roughnessMapBack = null;

  that.reflectionNormalMap = null;
  that.reflectionNormalMapIntensity = 1.0;
  that.reflectionNormalMapRepeat = 1.0;

  that.reflectionMapColorFront = toTinyColor('ffffffff').toThreeColor();
  that.reflectionMapFront = null;
  that.reflectionMapIntensityFront = 1.0;
  that.reflectionMapContrastFront = 1.0;
  that.reflectionMapRepeatFront = 1.0;

  that.reflectionMapColorBack = toTinyColor('ffffffff').toThreeColor();
  that.reflectionMapBack = null;
  that.reflectionMapIntensityBack = 1.0;
  that.reflectionMapContrastBack = 1.0;
  that.reflectionMapRepeatBack = 1.0;

  that.dispersionMap = null;
  that.dispersionMapIntensity = 1.0;
  that.dispersionMapRepeat = 1.0;


  //that.mapFront = null;
  //that.mapBack = null;

  //that.normalMapFront = null;
  //that.normalMapBack = null;

  //that.alphaMapFront = null;
  //that.alphaMapBack = null;

  //that.bumpMapFront = null;
  //that.bumpMapBack = null;
  //that.bumpScaleFront = 1.0;
  //that.bumpScaleBack = 1.0;

  //that.alphaThreshold = 0.0;
  //that.shadowOpacity = 1.0;
  //that.lightReflectivity = 1.0;

  that.clone = function() {
    let m = new MaterialPropertiesGem();
    m.materialType = 'gem';

    m.flatShading = that.flatShading;
    m.transparent = that.transparent;
  
    m.contrast = that.contrast;
    m.directSpecularContrast = that.directSpecularContrast;
    m.indirectSpecularContrast = that.indirectSpecularContrast;

    m.directSpecularIntensity = that.directSpecularIntensity;
    m.indirectSpecularIntensity = that.indirectSpecularIntensity;

    m.colorFront = that.colorFront;
    m.colorBack = that.colorBack;
  
    m.opacityFront = that.opacityFront;
    m.opacityBack = that.opacityBack;
  
    m.metalnessFront = that.metalnessFront;
    m.metalnessBack = that.metalnessBack;
    //m.metalnessMapFront = that.metalnessMapFront;
    //m.metalnessMapBack = that.metalnessMapBack;
  
    m.roughnessFront = that.roughnessFront;
    m.roughnessBack = that.roughnessBack;
    //m.roughnessMapFront = that.roughnessMapFront;
    //m.roughnessMapBack = that.roughnessMapBack;
  
    m.reflectionNormalMap = that.reflectionNormalMap;
    m.reflectionNormalMapIntensity = that.reflectionNormalMapIntensity;
    m.reflectionNormalMapRepeat = that.reflectionNormalMapRepeat;
  
    m.reflectionMapColorFront = that.reflectionMapColorFront;
    m.reflectionMapFront = that.reflectionMapFront;
    m.reflectionMapIntensityFront = that.reflectionMapIntensityFront;
    m.reflectionMapRepeatFront = that.reflectionMapRepeatFront;
    m.reflectionMapContrastFront = that.reflectionMapContrastFront;

    m.reflectionMapColorBack = that.reflectionMapColorBack;
    m.reflectionMapBack = that.reflectionMapBack;
    m.reflectionMapIntensityBack = that.reflectionMapIntensityBack;
    m.reflectionMapRepeatBack = that.reflectionMapRepeatBack;
    m.reflectionMapContrastBack = that.reflectionMapContrastBack;

    m.dispersionMap = that.dispersionMap;
    m.dispersionMapIntensity = that.dispersionMapIntensity;
    m.dispersionMapRepeat = that.dispersionMapRepeat;

    //m.mapFront = that.mapFront;
    //m.mapBack = that.mapBack;
  
    //m.normalMapFront = that.normalMapFront;
    //m.normalMapBack = that.normalMapBack;
  
    //m.alphaMapFront = that.alphaMapFront;
    //m.alphaMapBack = that.alphaMapBack;
  
    //m.bumpMapFront = that.bumpMapFront;
    //m.bumpMapBack = that.bumpMapBack;
    //m.bumpScaleFront = that.bumpScaleFront;
    //m.bumpScaleBack = that.bumpScaleBack;
  
    //m.alphaThreshold = that.alphaThreshold;
    //m.shadowOpacity = that.shadowOpacity;
    //m.lightReflectivity = that.lightReflectivity;

    return m;
  };
};

module.exports = MaterialPropertiesGem;
